import { useContext } from "react";
import { ValidationResult } from "sprint-common-web";

import { AppConfigContext } from "../../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../../core/data-access/useAuthorizedFetch";
import { CreateEntityResponse } from "../../common/CreateEntityResponse";
import { CloneEncodeSpecificationRequest } from "./requests/CloneEncodeSpecificationRequest";
import { CreateEncodeSpecificationMpeg1Request } from "./requests/CreateEncodeSpecificationMpeg1Request";
import { CreateEncodeSpecificationMpeg4Request } from "./requests/CreateEncodeSpecificationMpeg4Request";
import { UpdateEncodeSpecificationMpeg1Request } from "./requests/UpdateEncodeSpecificationMpeg1Request";
import { UpdateEncodeSpecificationMpeg4Request } from "./requests/UpdateEncodeSpecificationMpeg4Request";
import { ValidateFfmpegCommandRequest } from "./requests/ValidateFfmpegCommandRequest";
import { GetEncodeSpecificationByIdResponse } from "./responses/GetEncodeSpecificationByIdResponse";
import { GetEncodeSpecificationListResponse } from "./responses/GetEncodeSpecificationListResponse";
import { GetEncodeSpecificationsLookupResponse } from "./responses/GetEncodeSpecificationsLookupResponse";

export const useEncodeSpecificationApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const baseUrl = `${appConfigContext.ordersConfig.apiUrl}/encode-specification`;

    const { httpPost, httpPut, httpGet } = useAuthorizedFetch();

    const createEncodeSpecificationMpeg1 = async (
        request: CreateEncodeSpecificationMpeg1Request
    ): Promise<CreateEntityResponse> => {
        return httpPost(`${baseUrl}/create-mpeg1`, request);
    };

    const createEncodeSpecificationMpeg4 = async (
        request: CreateEncodeSpecificationMpeg4Request
    ): Promise<CreateEntityResponse> => {
        return httpPost(`${baseUrl}/create-mpeg4`, request);
    };

    const updateEncodeSpecificationMpeg1 = async (request: UpdateEncodeSpecificationMpeg1Request): Promise<void> => {
        return httpPut<void>(`${baseUrl}/update-mpeg1`, request);
    };

    const updateEncodeSpecificationMpeg4 = async (request: UpdateEncodeSpecificationMpeg4Request): Promise<void> => {
        return httpPut<void>(`${baseUrl}/update-mpeg4`, request);
    };

    const getAllEncodeSpecifications = async (): Promise<GetEncodeSpecificationListResponse> => {
        return httpGet<GetEncodeSpecificationListResponse>(`${baseUrl}/list`);
    };

    const getEncodeSpecificationsLookup = async (): Promise<GetEncodeSpecificationsLookupResponse> => {
        return httpGet<GetEncodeSpecificationsLookupResponse>(`${baseUrl}/get-all`);
    };

    const validateFfmpegCommand = async (
        request: ValidateFfmpegCommandRequest,
        signal?: AbortSignal
    ): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseUrl}/validate-ffmpeg-command`, request, signal);
    };

    const getEncodeSpecificationById = async (
        encodeSpecificationId: string
    ): Promise<GetEncodeSpecificationByIdResponse> => {
        return httpGet<GetEncodeSpecificationByIdResponse>(`${baseUrl}/${encodeSpecificationId}/get-by-id`);
    };

    const cloneEncodeSpecification = async (
        request: CloneEncodeSpecificationRequest
    ): Promise<CreateEntityResponse> => {
        return httpPost(`${baseUrl}/clone`, request);
    };

    return {
        createEncodeSpecificationMpeg1,
        createEncodeSpecificationMpeg4,
        updateEncodeSpecificationMpeg1,
        updateEncodeSpecificationMpeg4,
        getAllEncodeSpecifications,
        getEncodeSpecificationsLookup,
        validateFfmpegCommand,
        getEncodeSpecificationById,
        cloneEncodeSpecification
    };
};
